// ═══════════════════════════════════════════════════════════════
// Handoff — Templates
// Manage handoff note templates with customizable sections
// ═══════════════════════════════════════════════════════════════

import { useState } from 'react';
import {
  Title, Text, Paper, Group, Stack, Button, TextInput, Textarea, Badge,
  Modal, ActionIcon,
} from '../../components/gab/GabComponents';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { DEFAULT_TEMPLATE_SECTIONS } from '../../types';
import { useModulePermission } from '../../hooks/useModulePermission';

interface LocalTemplate {
  id: string;
  name: string;
  description: string;
  sections: Array<{ title: string; placeholder: string }>;
  isDefault: boolean;
}

export default function Templates() {
  const { canEdit } = useModulePermission('handoff');
  // For now, templates are managed locally until we wire up the templates table
  const [templates, setTemplates] = useState<LocalTemplate[]>([
    {
      id: 'default',
      name: 'Standard Shift Handoff',
      description: 'Default 5-section handoff template',
      sections: DEFAULT_TEMPLATE_SECTIONS,
      isDefault: true,
    },
  ]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<LocalTemplate | null>(null);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [sections, setSections] = useState<Array<{ title: string; placeholder: string }>>([
    { title: '', placeholder: '' },
  ]);

  const handleAddSection = () => {
    setSections([...sections, { title: '', placeholder: '' }]);
  };

  const handleRemoveSection = (i: number) => {
    setSections(sections.filter((_, idx) => idx !== i));
  };

  const handleSave = () => {
    if (!name.trim() || sections.filter(s => s.title.trim()).length === 0) return;
    const t: LocalTemplate = {
      id: editTemplate?.id || `template-${Date.now()}`,
      name: name.trim(),
      description: desc.trim(),
      sections: sections.filter(s => s.title.trim()),
      isDefault: false,
    };
    if (editTemplate) {
      setTemplates(templates.map(old => old.id === editTemplate.id ? t : old));
    } else {
      setTemplates([...templates, t]);
    }
    setCreateOpen(false); setEditTemplate(null);
    setName(''); setDesc(''); setSections([{ title: '', placeholder: '' }]);
  };

  const openEdit = (t: LocalTemplate) => {
    setEditTemplate(t);
    setName(t.name);
    setDesc(t.description);
    setSections([...t.sections]);
    setCreateOpen(true);
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Handoff Templates</Title>
        {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => {
          setEditTemplate(null); setName(''); setDesc(''); setSections([{ title: '', placeholder: '' }]);
          setCreateOpen(true);
        }}>New Template</Button>}
      </Group>

      <Stack gap="sm">
        {templates.map(t => (
          <Paper key={t.id} p="md" radius="sm" withBorder>
            <Group justify="space-between" mb="xs">
              <Group gap="xs">
                <Text fw={600}>{t.name}</Text>
                {t.isDefault && <Badge size="xs" color="blurple">Default</Badge>}
              </Group>
              {canEdit && (
              <Group gap="xs">
                <Button variant="subtle" size="xs" onClick={() => openEdit(t)}>Edit</Button>
                {!t.isDefault && (
                  <Button variant="subtle" size="xs" color="var(--color-error)"
                    onClick={() => setTemplates(templates.filter(x => x.id !== t.id))}>
                    Delete
                  </Button>
                )}
              </Group>
              )}
            </Group>
            {t.description && <Text size="xs" c="dimmed" mb="xs">{t.description}</Text>}
            <Group gap="xs" wrap="wrap">
              {t.sections.map((s, i) => (
                <Badge key={i} size="sm" variant="outline" color="#939598">{s.title}</Badge>
              ))}
            </Group>
          </Paper>
        ))}
      </Stack>

      {/* Create/Edit Modal */}
      <Modal opened={createOpen} onClose={() => { setCreateOpen(false); setEditTemplate(null); }}
        title={editTemplate ? 'Edit Template' : 'New Template'} size="lg">
        <Stack gap="sm">
          <TextInput label="Template Name" value={name} onChange={e => setName(e.target.value)} required />
          <Textarea label="Description" value={desc} onChange={e => setDesc(e.target.value)} minRows={1} />

          <Text fw={600} size="sm">Sections</Text>
          {sections.map((s, i) => (
            <Group key={i} gap="xs" align="flex-end">
              <TextInput label={`Section ${i + 1}`} placeholder="Section title"
                value={s.title} onChange={e => {
                  const updated = [...sections];
                  updated[i] = { ...updated[i], title: e.target.value };
                  setSections(updated);
                }} style={{ flex: 1 }} size="xs" />
              <TextInput placeholder="Placeholder text"
                value={s.placeholder} onChange={e => {
                  const updated = [...sections];
                  updated[i] = { ...updated[i], placeholder: e.target.value };
                  setSections(updated);
                }} style={{ flex: 1 }} size="xs" />
              {sections.length > 1 && (
                <ActionIcon variant="subtle" color="var(--color-error)" size="sm" onClick={() => handleRemoveSection(i)}>
                  <IconTrash size={12} />
                </ActionIcon>
              )}
            </Group>
          ))}
          <Button variant="subtle" size="xs" leftSection={<IconPlus size={12} />} onClick={handleAddSection}>
            Add Section
          </Button>

          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={() => { setCreateOpen(false); setEditTemplate(null); }}>Cancel</Button>
            <Button onClick={handleSave}>Save Template</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
